import React from "react";
import { format } from "date-fns";
import { ExternalLink, FileText } from "lucide-react";
import { EntityAttachment } from "../lib/api";

interface EntityAttachmentsListProps {
  attachments?: EntityAttachment[];
}

const formatFileSize = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageMime = (mimeType: string) => mimeType.startsWith("image/");

const EntityAttachmentsList: React.FC<EntityAttachmentsListProps> = ({ attachments }) => {
  const items = attachments ?? [];

  if (items.length === 0) {
    return <p className="text-sm text-gray-500">No attachments.</p>;
  }

  return (
    <div className="space-y-2">
      {items.map((attachment) => {
        const isImage = isImageMime(attachment.mimeType);

        return (
          <div
            key={attachment.id}
            className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-md border border-gray-200 bg-white overflow-hidden flex items-center justify-center">
              {isImage ? (
                <img
                  src={attachment.fileUrl}
                  alt={attachment.originalFileName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <FileText className="h-5 w-5 text-gray-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {attachment.typeName}
              </p>
              <p className="text-xs text-gray-600 truncate">{attachment.originalFileName}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {formatFileSize(attachment.fileSize)} · {attachment.mimeType} ·{" "}
                {format(new Date(attachment.createdAt), "MMM dd, yyyy HH:mm")}
              </p>
              <a
                href={attachment.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1 mt-1"
              >
                View file <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EntityAttachmentsList;
